/**
 * Sentry-like error reporting integration
 * Provides centralized error tracking with correlation IDs, breadcrumbs, and context
 */

import { generateCorrelationId, getSessionCorrelationId } from './correlationId';
import { logger as structuredLogger } from './structuredLogger';

interface ErrorContext {
  user_id?: string;
  org_id?: string;
  page_url?: string;
  user_agent?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

interface Breadcrumb {
  type: 'navigation' | 'ui' | 'http' | 'error' | 'info';
  category: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  level: 'debug' | 'info' | 'warning' | 'error';
}

interface ErrorReport {
  correlation_id: string;
  session_id: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  context: ErrorContext;
  breadcrumbs: Breadcrumb[];
  timestamp: string;
  environment: string;
  release?: string;
}

class ErrorReporter {
  private static instance: ErrorReporter;
  private breadcrumbs: Breadcrumb[] = [];
  private maxBreadcrumbs = 50;
  private context: ErrorContext = {};
  private isInitialized = false;
  private errorQueue: ErrorReport[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;

  static getInstance(): ErrorReporter {
    if (!ErrorReporter.instance) {
      ErrorReporter.instance = new ErrorReporter();
    }
    return ErrorReporter.instance;
  }

  /**
   * Initialize the error reporter with global handlers
   */
  init(options?: { release?: string; environment?: string }): void {
    if (this.isInitialized) return;
    
    this.context = {
      ...this.context,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };

    // Global error handler
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.captureException(event.error || new Error(event.message), {
          tags: { type: 'global_error' },
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason instanceof Error 
          ? event.reason 
          : new Error(String(event.reason));
        this.captureException(error, {
          tags: { type: 'unhandled_rejection' },
        });
      });
    }

    // Flush queue periodically
    this.flushInterval = setInterval(() => this.flush(), 30000);
    this.isInitialized = true;

    structuredLogger.info('ErrorReporter initialized', {
      correlation_id: generateCorrelationId('init'),
    });
  }

  /**
   * Set user context for error reports
   */
  setUser(user: { id?: string; email?: string; username?: string } | null): void {
    if (user) {
      this.context.user_id = user.id;
      this.context.extra = {
        ...this.context.extra,
        user_email: user.email,
        username: user.username,
      };
    } else {
      delete this.context.user_id;
      if (this.context.extra) {
        delete this.context.extra.user_email;
        delete this.context.extra.username;
      }
    }
  }

  /**
   * Set organization context
   */
  setOrg(orgId: string | null): void {
    if (orgId) {
      this.context.org_id = orgId;
    } else {
      delete this.context.org_id;
    }
  }

  /**
   * Add a breadcrumb for debugging context
   */
  addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'>): void {
    this.breadcrumbs.push({
      ...breadcrumb,
      timestamp: new Date().toISOString(),
    });

    // Keep only the last N breadcrumbs
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs = this.breadcrumbs.slice(-this.maxBreadcrumbs);
    }
  }

  /**
   * Capture and report an exception
   */
  captureException(
    error: Error,
    additionalContext?: Partial<ErrorContext>
  ): string {
    const correlationId = generateCorrelationId('err');
    const sessionId = getSessionCorrelationId();

    const report: ErrorReport = {
      correlation_id: correlationId,
      session_id: sessionId,
      error: {
        name: error.name,
        message: error.message,
        stack: import.meta.env.DEV ? error.stack : this.sanitizeStack(error.stack),
      },
      context: {
        ...this.context,
        ...additionalContext,
        page_url: typeof window !== 'undefined' ? window.location.href : this.context.page_url,
        tags: {
          ...this.context.tags,
          ...additionalContext?.tags,
        },
      },
      breadcrumbs: [...this.breadcrumbs],
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE,
    };

    // Log structured error
    structuredLogger.error(`[${correlationId}] ${error.message}`, error, {
      correlation_id: correlationId,
      session_id: sessionId,
      org_id: this.context.org_id,
      user_id: this.context.user_id,
    });

    // Queue for sending to backend
    this.errorQueue.push(report);

    // Immediate flush for critical errors
    if (this.isCriticalError(error)) {
      this.flush();
    }

    return correlationId;
  }

  /**
   * Capture a message as an event
   */
  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info'
  ): string {
    const correlationId = generateCorrelationId('msg');

    this.addBreadcrumb({
      type: 'info',
      category: 'message',
      message,
      level,
    });

    structuredLogger[level](message, {
      correlation_id: correlationId,
      session_id: getSessionCorrelationId(),
      org_id: this.context.org_id,
      user_id: this.context.user_id,
    });

    return correlationId;
  }

  /**
   * Track navigation for breadcrumbs
   */
  trackNavigation(from: string, to: string): void {
    this.addBreadcrumb({
      type: 'navigation',
      category: 'navigation',
      message: `Navigated from ${from} to ${to}`,
      data: { from, to },
      level: 'info',
    });
  }

  /**
   * Track UI interactions for breadcrumbs
   */
  trackUIAction(action: string, target: string, data?: Record<string, unknown>): void {
    this.addBreadcrumb({
      type: 'ui',
      category: 'ui.click',
      message: `${action} on ${target}`,
      data,
      level: 'info',
    });
  }

  /**
   * Track HTTP requests for breadcrumbs
   */
  trackHTTP(
    method: string,
    url: string,
    status?: number,
    duration?: number
  ): void {
    this.addBreadcrumb({
      type: 'http',
      category: 'http',
      message: `${method} ${url}`,
      data: { method, url, status, duration_ms: duration },
      level: status && status >= 400 ? 'error' : 'info',
    });
  }

  /**
   * Flush error queue to backend
   */
  private async flush(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    // In production, send to error tracking endpoint
    if (import.meta.env.PROD) {
      try {
        // TODO: Replace with actual error tracking endpoint
        // await fetch('/api/errors', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ errors }),
        // });
        
        // For now, store in console for debugging
        console.groupCollapsed(`[ErrorReporter] Flushed ${errors.length} errors`);
        errors.forEach(err => console.log(JSON.stringify(err, null, 2)));
        console.groupEnd();
      } catch (e) {
        // Re-queue on failure
        this.errorQueue.unshift(...errors);
      }
    } else {
      // In development, just log
      errors.forEach(err => {
        console.groupCollapsed(`[ErrorReporter] ${err.error.name}: ${err.error.message}`);
        console.log('Correlation ID:', err.correlation_id);
        console.log('Context:', err.context);
        console.log('Breadcrumbs:', err.breadcrumbs);
        console.groupEnd();
      });
    }
  }

  /**
   * Sanitize stack trace for production (remove source maps references)
   */
  private sanitizeStack(stack?: string): string | undefined {
    if (!stack) return undefined;
    return stack
      .split('\n')
      .slice(0, 10) // Limit stack depth
      .join('\n');
  }

  /**
   * Check if error is critical (requires immediate attention)
   */
  private isCriticalError(error: Error): boolean {
    const criticalPatterns = [
      /chunk.*failed/i,
      /out of memory/i,
      /maximum call stack/i,
      /network.*error/i,
      /authentication.*failed/i,
    ];
    return criticalPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  /**
   * Cleanup on unmount
   */
  cleanup(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }

  /**
   * Get recent breadcrumbs for debugging
   */
  getBreadcrumbs(): Breadcrumb[] {
    return [...this.breadcrumbs];
  }

  /**
   * Get current context
   */
  getContext(): ErrorContext {
    return { ...this.context };
  }
}

export const errorReporter = ErrorReporter.getInstance();
